/* ════════════════════════════════════════════════════════════════════════ */
/* #region(collapsed) Developer Documentation */
/**
  @license
  # COPYRIGHT NOTICE:
    © CROUIN 2024. All rights reserved. Powered by MaxDesign.
    ⚠ FR : CONTENU PROTÉGÉ PAR LES DROITS D'AUTEURS (article L. 111-1 du code de la propriété intellectuelle). Cette ressource ne peut pas être utilisée, diffusée ou exploitée sans l'approbation de ses auteurs. Pour plus d'informations, veuillez contacter admin(at)maxdesign.expert
    ⚠ EN : CONTENT PROTECTED WITH AUTHOR COPYRIGHTS (section L. 111-1 of the intellectual property code). This resource may not be used, distributed or exploited without the approval of its authors. For any information request, please contact admin(at)maxdesign.expert
 
  # MORE INFORMATION:
    Contact us at admin(at)maxdesign.expert
*/
/* #endregion ═════════════════════════════════════════════════════════════ */

class ProductProperties extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.form = this.section?.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;
    this.settings = JSON.parse(this.querySelector('[type="application/json"]').innerText);
    this.filters = this.settings.filters;
    this.fields = [...this.querySelectorAll('fieldset')];
    this.addEventListener('input', this.onInputChange.bind(this));
    this.addEventListener('mousedown', this.onMouseDown.bind(this));
    if (Object.keys(this.filters.variant).length)
      this.section.addEventListener('change', this.onVariantChange.bind(this));
    this.updateFields();
    this.updateFormId();
  }

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) User Events */

  onVariantChange(e) {
    const target = e.target;
    if (target.name == 'id') this.updateFields(+target.value);
  }
  /**
   * We add a maxchar counter for text input
   * Update visibility and disable status on change
   * @param {Object} e - input event
   */
  onInputChange(e) {
    const target = e.target;
    const field = target.closest('fieldset');
    const { maxLength, value, type } = target;
    if (type == 'text') {
      if (value.length > maxLength) target.value = value.slice(0, maxLength);
      target.parentElement.dataset.counter = `${target.value.length}/${maxLength}`;
    }
    field.dataset.value = value;
    this.updateFields();
  }

  /**
   * Add focus on mousedown to avoid flickering effect when changing input selection
   */
  onMouseDown(e) {
    const target = e.target;
    if (target.closest('fieldset')) {
      e.preventDefault();
      target.focus();
    }
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Update Element */
  updateFormId() {
    const id = this.form.getAttribute('id');
    this.querySelectorAll('input').forEach(input => input.setAttribute('form', id));
  }

  /**
   * Toggle fields visibility based on property and/or variant filters
   * We disabled fields if the filterField does not exist, or if it does not contain the matching value or if it is disabled
   * @param {Number} variantId - selected variant id
   */
  updateFields(variantId) {
    variantId = variantId || +this.section.querySelector('[name="id"]')?.value;
    const fixIOSEllipsisBug = field => {
      if (!field.disabled) {
        const legend = field.querySelector(':scope > legend');
        if (legend) {
          legend.style.display = 'none';
          legend.offsetHeight;
          legend.style.display = '';
        }
      }
    };
    this.settings.handles.forEach(handle => {
      const field = this.fields.find(field => field.dataset.handle == handle);
      const propFilter = this.filters.property[handle];
      const variantFilter = this.filters.variant[handle];
      let disabled = false;
      if (propFilter) {
        const filterField = this.fields.find(field => field.dataset.handle == propFilter.handle);
        disabled = filterField && (!propFilter.values.includes(filterField.dataset.value) || filterField.disabled);
        field.disabled = disabled;
        if (/iPad|iPhone|Macintosh/.test(navigator.userAgent)) setTimeout(() => fixIOSEllipsisBug(field));
      }
      if (variantFilter && variantId && !disabled) {
        field.disabled = !variantFilter.ids.includes(variantId);
        if (/iPad|iPhone|Macintosh/.test(navigator.userAgent)) setTimeout(() => fixIOSEllipsisBug(field));
      }
    });
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */

  /* ════════════════════════════════════════════════════════════════════════ */
  /* #region(collapsed) Getters & Setters */
  get ctaNode() {
    return (this._ctaNode =
      this._ctaNode ||
      this.form.querySelector('[type="submit"]') ||
      this.section.querySelector(`[type="submit"][form="${this.form.getAttribute('id')}"]`));
  }
  /* #endregion ═════════════════════════════════════════════════════════════ */
}
customElements.define('product-properties-maxdesign', ProductProperties);
